import {
  ActedForList,
  ActedForView,
  ActForPeople,
  StagedFile,
  WaitingOnOthers,
  type DocumentView,
  type EditActedForNote,
  type QuestionActedFor,
  type SettleActedFor,
} from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// APP-148: a Stock Admin acting for an Executive or a Sector Lead. The step's staged files, who
// may be named, Waiting on others and what the reader did for others. Sample-roles Preview gets
// 404 or nothing from these reads (R11): its pages show their own samples instead.

/** Ids come from the server or the address bar: only a UUID ever reaches a request path. */
const Id = z.uuid();

function path(id: string): string {
  return encodeURIComponent(Id.parse(id));
}

export const actingForKeys = {
  all: ['acting-for'] as const,
  people: ['acting-for', 'people'] as const,
  doneByMe: (days: number) => ['acting-for', 'done-by-me', days] as const,
  doneForMe: (days: number | null, page: number, size: number | null) =>
    ['acting-for', 'done-for-me', days, page, size] as const,
  one: (id: string) => ['acting-for', 'one', id] as const,
  award: (requestId: string) => ['acting-for', 'award', requestId] as const,
};

/**
 * A Stock Admin's Waiting on others (`GET /api/v1/home/waiting-on-others`, D2): the Executives'
 * and Sector Leads' work, oldest first, with the switches and the last thing anyone did for
 * someone. Under Home's key, so every decision (which refreshes Home) refreshes it too. 404 for
 * anyone who isn't on the Stock Admin team, and in sample-roles Preview.
 */
export const waitingOnOthersQuery = queryOptions({
  queryKey: ['home', 'waiting-on-others'] as const,
  queryFn: ({ signal }) =>
    request('/api/v1/home/waiting-on-others', { schema: WaitingOnOthers, signal }),
  staleTime: 30_000,
});

/** Who a Stock Admin may name today, and the two switches (Stock Admins only; 404 otherwise). */
export const actForPeopleQuery = queryOptions({
  queryKey: actingForKeys.people,
  queryFn: ({ signal }) => request('/api/v1/acting-for/people', { schema: ActForPeople, signal }),
  staleTime: 30_000,
});

/** What the reader did for others in the last `days` days, newest first (the first page). */
export function doneByMeQuery(days = 30) {
  return queryOptions({
    queryKey: actingForKeys.doneByMe(days),
    queryFn: ({ signal }) =>
      request(`/api/v1/acting-for/done-by-me?days=${String(days)}`, {
        schema: ActedForList,
        signal,
      }),
    staleTime: 30_000,
  });
}

/**
 * What was done for the reader, newest first (APP-148 D2): Home's card reads the last `days` days,
 * `/done-for-you` every page of the whole list. Empty for someone nobody has acted for, and in
 * sample-roles Preview.
 */
export function doneForMeQuery(opts: { days?: number; page?: number; size?: number } = {}) {
  const { days, page = 1, size } = opts;
  const search = new URLSearchParams();
  if (days !== undefined) search.set('days', String(days));
  if (page !== 1) search.set('page', String(page));
  if (size !== undefined) search.set('size', String(size));
  const query = search.toString();
  return queryOptions({
    queryKey: actingForKeys.doneForMe(days ?? null, page, size ?? null),
    queryFn: ({ signal }) =>
      request(`/api/v1/acting-for/done-for-me${query === '' ? '' : `?${query}`}`, {
        schema: ActedForList,
        signal,
      }),
    staleTime: 30_000,
  });
}

/** One thing done for someone, by its ID (the email's "Not what you asked?" link). */
export function actedForQuery(actedForId: string) {
  return queryOptions({
    queryKey: actingForKeys.one(actedForId),
    queryFn: ({ signal }) =>
      request(`/api/v1/acting-for/${path(actedForId)}`, { schema: ActedForView, signal }),
    staleTime: 10_000,
  });
}

/**
 * "Not what you asked?" (D6, D17): the person it was for says what's wrong. The award, if the
 * action left it going on, is held before its next step; the answer is the row as it is now.
 */
export function questionActedFor(
  actedForId: string,
  body: QuestionActedFor,
): Promise<ActedForView> {
  return request(`/api/v1/acting-for/${path(actedForId)}/question`, {
    method: 'POST',
    body,
    schema: ActedForView,
  });
}

/**
 * Settles "Not what you asked?" (D17): confirm it as it is (the hold lifts), or return the award to
 * whoever entered it, with the settler's own reason (R36). Any Executive, or the award's Sector
 * Lead, as the row's `can.settle` says.
 */
export function settleActedFor(actedForId: string, body: SettleActedFor): Promise<ActedForView> {
  return request(`/api/v1/acting-for/${path(actedForId)}/settle`, {
    method: 'POST',
    body,
    schema: ActedForView,
  });
}

/**
 * Adds a file to what was done for someone, later (D24): stored, virus-checked and audited on the
 * award at once, by whoever adds it (the Stock Admin team and the System Admins).
 */
export async function addActedForFile(actedForId: string, file: File): Promise<DocumentView> {
  const form = new FormData();
  form.append('file', file, file.name);
  const { document } = await request(`/api/v1/acting-for/${path(actedForId)}/files`, {
    method: 'POST',
    body: form,
    schema: StagedFile,
  });
  return document;
}

/** A new version of one of its files, which the old one points on to (D24). */
export async function replaceActedForFile(
  actedForId: string,
  documentId: string,
  file: File,
): Promise<DocumentView> {
  const form = new FormData();
  form.append('file', file, file.name);
  const { document } = await request(
    `/api/v1/acting-for/${path(actedForId)}/files/${path(documentId)}/replace`,
    { method: 'POST', body: form, schema: StagedFile },
  );
  return document;
}

/** Removes one of its files: off the card, kept in the history with the reason (D24). */
export async function removeActedForFile(
  actedForId: string,
  documentId: string,
  reason: string,
): Promise<void> {
  await request(`/api/v1/acting-for/${path(actedForId)}/files/${path(documentId)}`, {
    method: 'DELETE',
    body: { reason },
    schema: z.undefined(),
  });
}

/** Changes the note kept with it (D24); an empty note takes it off. Audited as "Note". */
export function editActedForNote(
  actedForId: string,
  body: EditActedForNote,
): Promise<ActedForView> {
  return request(`/api/v1/acting-for/${path(actedForId)}/note`, {
    method: 'PUT',
    body,
    schema: ActedForView,
  });
}

/** What was done for someone on an award, for those who may see it (empty for anyone else). */
export function actedForAwardQuery(requestId: string) {
  return queryOptions({
    queryKey: actingForKeys.award(requestId),
    queryFn: ({ signal }) =>
      request(`/api/v1/acting-for/award/${path(requestId)}`, {
        schema: z.array(ActedForView),
        signal,
      }),
    staleTime: 10_000,
  });
}

/**
 * Stages one file while the step is open (D14): stored and virus-checked, owned by the uploader,
 * attached only when the action confirms. Each step stages its own copy: a file is never reused.
 */
export async function stageFile(file: File, signal?: AbortSignal): Promise<DocumentView> {
  const form = new FormData();
  form.append('file', file, file.name);
  const { document } = await request('/api/v1/acting-for/files', {
    method: 'POST',
    body: form,
    schema: StagedFile,
    ...(signal !== undefined && { signal }),
  });
  return document;
}

/** A staged file as it stands now: its virus check, read while it's pending (R47). */
export async function readStagedFile(
  documentId: string,
  signal?: AbortSignal,
): Promise<DocumentView> {
  const { document } = await request(`/api/v1/acting-for/files/${path(documentId)}`, {
    schema: StagedFile,
    ...(signal !== undefined && { signal }),
  });
  return document;
}

/** Removes a staged file the step no longer wants (D14: never used, so its bytes go too). */
export async function unstageFile(documentId: string): Promise<void> {
  await request(`/api/v1/acting-for/files/${path(documentId)}`, {
    method: 'DELETE',
    schema: z.undefined(),
  });
}

/** Where a file kept with an action downloads from (clean or not needing a check only). */
export function actedForFileHref(actedForId: string, documentId: string): string {
  return `/api/v1/acting-for/${path(actedForId)}/files/${path(documentId)}/content`;
}
